// R1b-γ — Tabbed Financial shell. Same pattern as PeopleRolesShell.
// URL is source of truth: /admin/financial/{overview|billing|contracts|payments}.
// Zero fake numbers. Empty states are honest.
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutDashboard, CreditCard, FileText, Receipt } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import FinancialOverviewTab from "./tabs/FinancialOverviewTab";
import FinancialBillingTab from "./tabs/FinancialBillingTab";
import FinancialContractsTab from "./tabs/FinancialContractsTab";
import FinancialPaymentsTab from "./tabs/FinancialPaymentsTab";

const TABS = [
  { key: "overview",  label: "Overview",  icon: LayoutDashboard, Body: FinancialOverviewTab, testid: "financial-tab-overview" },
  { key: "billing",   label: "Billing",   icon: CreditCard,      Body: FinancialBillingTab,  testid: "financial-tab-billing" },
  { key: "contracts", label: "Contracts", icon: FileText,        Body: FinancialContractsTab,testid: "financial-tab-contracts" },
  { key: "payments",  label: "Payments",  icon: Receipt,         Body: FinancialPaymentsTab, testid: "financial-tab-payments" },
];

export default function FinancialShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tab: tabParam } = useParams();
  const current = TABS.find((t) => t.key === tabParam) || TABS[0];
  const CurrentBody = current.Body;

  return (
    <AdminAppShell
      activeNav="financial"
      title="Financial"
      subtitle="Overview · Billing · Contracts · Payments"
      breadcrumbs={[
        { label: "Owner Command Center", to: "/owner" },
        { label: "Financial", to: "/admin/financial/overview" },
        { label: current.label, to: null },
      ]}
      user={user}
    >
      <div
        role="tablist"
        aria-label="Financial sections"
        data-testid="financial-tablist"
        style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid #E8E4D9", overflowX: "auto" }}
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === current.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`financial-panel-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={t.testid}
              onClick={() => navigate(`/admin/financial/${t.key}`)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  const dir = e.key === "ArrowRight" ? 1 : -1;
                  const idx = TABS.findIndex((x) => x.key === current.key);
                  const next = TABS[(idx + dir + TABS.length) % TABS.length];
                  navigate(`/admin/financial/${next.key}`);
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 16px", minHeight: 44,
                background: isActive ? "#FFFFFF" : "transparent",
                border: 0, borderBottom: isActive ? "2px solid #B8892B" : "2px solid transparent",
                marginBottom: "-1px",
                fontFamily: '"Inter Tight", system-ui, sans-serif',
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? "#0F0F10" : "#4A4A4C",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              <Icon size={14} aria-hidden="true" /> {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={`financial-panel-${current.key}`} data-testid={`financial-panel-${current.key}`}>
        <CurrentBody />
      </div>
    </AdminAppShell>
  );
}
